/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject } from 'preact';
import { useRef, useCallback, useEffect, useState, useMemo } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { containsKey, Keys, isKeyDefined } from '../utils/UNSAFE_keys';
import {
  DataState,
  ItemContext,
  ListItemContext,
  Metadata,
  Range,
  SelectionDetail
} from '../UNSAFE_Collection';
import { LoadMoreCollection, LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';
import { VirtualizedCollection, PLACEHOLDER_STYLE_CLASS } from '../PRIVATE_VirtualizedCollection';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { ListViewItem, ITEM_SELECTOR, MemoizeListViewItem } from './ListItem';
import { SelectionRangeDetail, useSelection } from '../hooks/PRIVATE_useSelection';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '../hooks/PRIVATE_useTabbableModeSet';
import { useItemAction } from '../hooks/PRIVATE_useItemAction';
import { SkeletonContainer } from './SkeletonContainer';
import { Skeleton } from '../UNSAFE_Skeleton';
import { Flex } from '../UNSAFE_Flex';
import {
  getFirstVisibleKey,
  getPrevNextKey,
  findElementByKey,
  keyExtractor
} from '../utils/PRIVATE_collectionUtils';
import { Selector } from '../UNSAFE_Selector';
import { Props } from '../UNSAFE_ListView';
import { getListViewRoles } from './ListRoles';
import { useHandleRemoveCurrentKey } from './useHandleRemoveCurrentKey';
import { findSuggestions, Sparkle } from './Sparkle';
import { useCollectionGestureContext } from '../hooks/PRIVATE_useCollectionGestureContext';
import { listStyles } from './themes/ListStyles.css';

/**
 * Component that renders items as a flat list.
 * In order to maximize performance, only items that are visible in the viewport are rendered.
 */
export function List<K extends string | number, D>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  allowTabbableMode = true,
  children,
  currentKey,
  data,
  gridlines,
  loadingIndicator = defaultLoadingIndicator,
  onCurrentKeyChange,
  onLoadRange,
  onSelectionChange,
  onItemAction,
  rangeExtractor,
  role = 'grid',
  selectedKeys = emptyKeys as Keys<K>,
  selectionMode = 'none',
  viewportConfig,
  currentItemVariant = 'none',
  promotedSection,
  isVirtualized = true,
  customItemRenderer,
  eventHandlers,
  scrollToVisibleOffset
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listRoles = useMemo(() => getListViewRoles(role), [role]);

  // tracking the anchor key which is used for shift+click selection
  const anchorKey = useRef<K | undefined>();

  // tracking pending selection/current key that is not in current data
  const pendingSelection = useRef<{ detail: SelectionRangeDetail<K>; range: Range }>();
  const pendingCurrentKey = useRef<K>();

  const [active, setActive] = useState<boolean>(false);

  // tracking sparkle height
  const [sparkleHeight, setSparkleHeight] = useState<number>(0);

  const showGridline = (index: number) => {
    // show bottom gridlines for each item, and for the last item if specified
    return (
      gridlines?.item === 'visible' &&
      (index + 1 !== data?.totalSize || gridlines?.bottom === 'visible')
    );
  };

  const showGridlineTop = () => {
    // show the top gridline for the first item
    return gridlines?.item === 'visible' && gridlines?.top === 'visible';
  };

  const ariaMultiSelectable = selectionMode === 'none' ? undefined : selectionMode === 'multiple';
  const ariaRowCount =
    data === null ? undefined : data.sizePrecision === 'exact' ? data.totalSize : -1;

  const classes = classNames([listStyles.base]);

  // find out whether the current key was there before in prev render
  // and adjust current key as needed.  This should be run before other
  // useEffect(s) that could also alter current key.
  useHandleRemoveCurrentKey(data, currentKey, onCurrentKeyChange);

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    selectionMode !== 'multiple',
    getPrevNextKey(rootRef.current, currentKey, true, ITEM_SELECTOR),
    getPrevNextKey(rootRef.current, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    onCurrentKeyChange
  );

  const listViewContains = (elem: Element | null) => {
    return !!rootRef.current?.contains(elem);
  };

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(listViewContains, [
    'ArrowUp',
    'ArrowDown'
  ]);

  let pendingFocusRingChange = false;
  const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
    listViewContains,
    (element) => {
      // we don't want List to go into Tabbable mode when user clicks on expander
      return isExpander(element) ? null : (keyExtractor(element, ITEM_SELECTOR) as K);
    },
    currentKey,
    // handle clean-up on disabling of the mode
    (target: EventTarget | null, isKeyboardEvent) => {
      if (listViewContains(target as Element)) {
        // delay focus reset for keyboard disable so focus is reset to ListView after the FocusTrap blur lifecycle completes.
        // pointer initiated changes will fire the onBlur organically and delaying the focus, just generates an unwanted second blur
        if (isKeyboardEvent) {
          // do not show a focus ring during this timeOut
          pendingFocusRingChange = true;
          setTimeout(() => {
            rootRef.current?.focus();
          }, 0);
        } else {
          rootRef.current?.focus();
        }
      }
      // otherwise target is not in list (including body, which target should be null in that case)
    },
    // set currentKey when mode is enabled
    (key: K) => {
      onCurrentKeyChange && onCurrentKeyChange({ value: key });
    }
  );

  useEffect(() => {
    if (currentKey != null && rootRef.current) {
      const elem = findElementByKey(rootRef.current, currentKey, ITEM_SELECTOR);
      if (elem) {
        const activeDescendant = rootRef.current.getAttribute('aria-activedescendant');
        const cell = elem.querySelector(`[role=${listRoles.cell}]`);
        if (cell && activeDescendant !== cell.id) {
          // update aria-activedescendant for screenreader
          rootRef.current.setAttribute('aria-activedescendant', cell.id);

          const scroller = viewportConfig?.scroller();
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          // make sure item is visible
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    }
    // listen to data because the current item would re-render when the virtualizer
    // is fetching data or updating the data, i.e. the id of current item would be updated,
    // so the currentKey itself couldn't guarantee the root has latest aria-activedescendant
  }, [currentKey, data, listRoles.cell, viewportConfig]);

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange) {
        if (detail.value.all === false && detail.value.keys.size > 0) {
          // the last key selected by a user gesture is the anchor key
          anchorKey.current = Array.from(detail.value.keys.values()).pop();
        }
        onSelectionChange(detail);
      }
    },
    [anchorKey, onSelectionChange]
  );

  const handleSelectionRangeChange = useCallback(
    (detail: SelectionRangeDetail<K>) => {
      if (data && onSelectionChange) {
        const value = handleSelectionRange(detail, data);
        if (Array.isArray(value)) {
          onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
        } else {
          pendingSelection.current = {
            detail: detail,
            range: { offset: data.offset, count: data.data.length }
          };
          onLoadRange(value);
        }
      }
    },
    [data, onSelectionChange, onLoadRange]
  );

  const handleInitialFocus = useCallback(() => {
    if (
      rootRef.current &&
      onCurrentKeyChange &&
      (!allowTabbableMode || isCurrentTabbableKey(undefined))
    ) {
      const firstKey =
        pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, ITEM_SELECTOR) as K);
      if (isKeyDefined(firstKey)) {
        onCurrentKeyChange({ value: firstKey });
      }
    }
  }, [allowTabbableMode, onCurrentKeyChange, isCurrentTabbableKey]);

  /**
   * Used by allowTabbableMode = false, to prevent focus from getting onto checkboxes on click
   * @param event
   */
  const handleSelectorFocusCapture = useCallback(
    (event: FocusEvent) => {
      const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
      if (key && (event.relatedTarget as HTMLElement)?.getAttribute('type') === 'checkbox') {
        rootRef.current?.focus();
        if (onCurrentKeyChange && key) {
          onCurrentKeyChange({ value: key as K });
        }
      }
    },
    [onCurrentKeyChange]
  );

  const handleFocus = useCallback(
    (event: FocusEvent) => {
      if (isExpander(event.target as HTMLElement)) {
        rootRef.current?.focus();
        const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
        if (onCurrentKeyChange && key) {
          onCurrentKeyChange({ value: key as K });
        }
        return;
      }
      !isKeyDefined(currentKey) && onCurrentKeyChange && handleInitialFocus();
      !allowTabbableMode && handleSelectorFocusCapture(event);
    },
    [
      allowTabbableMode,
      currentKey,
      handleInitialFocus,
      handleSelectorFocusCapture,
      onCurrentKeyChange
    ]
  );

  const value = useCollectionGestureContext();

  const { selectionProps } = useSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode,
    false,
    'replace',
    value === 'embedded',
    handleSelectionChange,
    anchorKey.current,
    currentKey,
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, ITEM_SELECTOR),
    undefined,
    (key: K) => {
      if (rootRef.current) {
        const elem = findElementByKey(rootRef.current, key, ITEM_SELECTOR);
        if (elem) {
          const scroller = viewportConfig?.scroller();
          const offset = scrollToVisibleOffset ? scrollToVisibleOffset(rootRef.current) : 0;
          scrollToVisible(elem, scroller != null ? scroller : rootRef.current, offset);
        }
      }
    },
    handleSelectionRangeChange
  );

  if (data && pendingSelection.current !== undefined && onSelectionChange) {
    const keys = handleSelectionRange(pendingSelection.current.detail, data);
    if (Array.isArray(keys)) {
      onSelectionChange({ value: { all: false, keys: new Set(keys) }, target: null });
    }
    const range = pendingSelection.current
      ? pendingSelection.current.range
      : { offset: data.offset, count: data.data.length };
    data = adjustDataState(data, range);
    pendingSelection.current = undefined;
    onLoadRange(range);
  }

  const suggestions = useMemo(
    () => data && promotedSection && findSuggestions(data.data, promotedSection.count),
    [data, promotedSection]
  );

  // TODO: check suggestions before Sparkle would cause a scrolling issue
  const sparkleIndicator = <Sparkle sparkleHeight={sparkleHeight}></Sparkle>;

  const itemActionProps = useItemAction(
    currentKey,
    data,
    onItemAction,
    ITEM_SELECTOR,
    value === 'embedded'
  );

  useEffect(() => {
    // update sparkleHeight only when we have suggestions
    // avoid unnecessary iterating through elements
    if (suggestions) {
      const placeholder = rootRef.current?.querySelector(
        '.' + PLACEHOLDER_STYLE_CLASS
      ) as HTMLElement;
      let height = placeholder?.offsetHeight || 0;
      const suggestionItems = rootRef.current?.querySelectorAll('[data-oj-suggestion]');
      suggestionItems?.forEach((item) => (height += (item as HTMLElement).offsetHeight));
      setSparkleHeight(height);
    }
  }, [suggestions]);

  // returns an ListItemContext based on ItemContext (added metadata and selector)
  const getItemContext = useCallback(
    (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => {
      const selectorRenderer =
        selectionMode === 'multiple'
          ? () => (
              <Selector
                onChange={handleSelectionChange}
                rowKey={context.data.metadata.key}
                selectedKeys={selectedKeys}></Selector>
            )
          : undefined;

      return {
        index: context.index,
        data: context.data.data,
        metadata: context.data.metadata,
        selector: selectorRenderer
      };
    },
    [selectionMode, selectedKeys, handleSelectionChange]
  );

  const ItemComponent = isVirtualized ? ListViewItem : MemoizeListViewItem;

  const childrenComponent = (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => {
    const listItemContext = getItemContext(context);
    const suggestion = suggestions?.get(listItemContext.metadata.key);
    const isTabbable = allowTabbableMode && isCurrentTabbableKey(listItemContext.metadata.key);
    const isFocused = currentKey === listItemContext.metadata.key && !isTabbable;
    const isActive = isFocused && active;
    const isSelected = containsKey(selectedKeys, listItemContext.metadata.key);
    const isFocusRingVisible = isFocused && showFocusRing && !pendingFocusRingChange;
    const listItemFunc = (context: ListItemContext<K, D>, otherProps?: any) => (
      <ItemComponent
        key={listItemContext.metadata.key}
        isFocused={isFocused}
        isFocusRingVisible={isFocusRingVisible}
        isActive={isActive}
        isGridlineVisible={showGridline(listItemContext.index)}
        isSelected={isSelected}
        isTabbable={allowTabbableMode && isCurrentTabbableKey(listItemContext.metadata.key)}
        itemIndex={listItemContext.index}
        itemKey={listItemContext.metadata.key}
        itemData={listItemContext.data}
        role={role}
        selectionMode={selectionMode}
        currentItemVariant={currentItemVariant}
        {...(suggestion && { suggestion })}
        {...(listItemContext.index === 0 && { isTopGridlineVisible: showGridlineTop() })}
        {...otherProps}>
        {children(context)}
      </ItemComponent>
    );

    // if customItemRenderer is specified (ExpandableList and GroupedList)
    // create a custom context with additional info including a function
    // that helps create the default item component
    if (customItemRenderer) {
      const customRendererContext = {
        listItemContext,
        isFocused,
        isFocusRingVisible,
        isSelected,
        isTabbable,
        isActive,
        defaultListItem: listItemFunc
      };
      return customItemRenderer(customRendererContext);
    }
    return listItemFunc(listItemContext);
  };

  viewportConfig = getViewportConfig(rootRef, viewportConfig);

  /**
   * Need to track pointer down element to set currentItem when initial focus happens
   */
  const handlePointerDown = useCallback((event: PointerEvent) => {
    const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
    if (isKeyDefined(key)) pendingCurrentKey.current = key as K;
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ' ' && currentKey && selectionMode !== 'none') {
        setActive(true);
      }
    },
    [currentKey, selectionMode, setActive]
  );

  const handleKeyUp = useCallback(() => {
    setActive(false);
  }, [setActive]);

  // if data is not specified, listview should show loading indicator
  // todo: replace placeholder with actual SkeletonContainer component (Ash is working on)
  return (
    <div
      {...mergeProps(
        allowTabbableMode ? tabbableModeProps : {},
        currentKeyProps,
        focusRingProps,
        selectionProps,
        itemActionProps,
        {
          onFocus: handleFocus,
          onPointerDown: handlePointerDown,
          onKeyDown: handleKeyDown,
          onKeyUp: handleKeyUp
        },
        eventHandlers !== undefined ? eventHandlers : {}
      )}
      role={role}
      aria-rowcount={ariaRowCount}
      aria-colcount={1}
      ref={rootRef}
      class={classes}
      tabIndex={0}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-multiselectable={ariaMultiSelectable}>
      {data == null ? (
        loadingIndicator
      ) : isVirtualized ? (
        <VirtualizedCollection
          data={data}
          itemSelector={ITEM_SELECTOR}
          loadMoreIndicator={defaultLoadMoreIndicator}
          onLoadRange={onLoadRange}
          rangeExtractor={rangeExtractor}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </VirtualizedCollection>
      ) : (
        <LoadMoreCollection
          data={data.data}
          loadMoreIndicator={defaultLoadMoreIndicator}
          hasMore={data.sizePrecision === 'atLeast' && data.data.length <= data.totalSize}
          onLoadMore={() => {
            data && onLoadRange({ offset: 0, count: data.data.length + 25 });
          }}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </LoadMoreCollection>
      )}
    </div>
  );
}

const isExpander = (elem: HTMLElement) => {
  if (elem.parentElement && elem.parentElement.classList.contains('oj-listview-expander')) {
    return true;
  }
  return false;
};

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
const scrollToVisible = (elem: Element, scroller: Element, offset: number) => {
  if (elem && scroller) {
    if ((elem as any).scrollIntoViewIfNeeded) {
      // for Safari, we'll need the non-standard scrollIntoViewIfNeeded
      (elem as any).scrollIntoViewIfNeeded();
    } else {
      elem.scrollIntoView({ block: 'nearest' });
    }
    if (offset > 0) {
      const scrollerBounds = scroller.getBoundingClientRect();
      const elemBounds = elem.getBoundingClientRect();
      const diff = scrollerBounds.top + offset - elemBounds.top;
      if (diff > 0) {
        scroller.scrollTop = scroller.scrollTop - diff;
      }
    }
  }
};

const getViewportConfig = (rootRef: RefObject<HTMLElement>, config?: ViewportConfig) => {
  return (
    config ?? {
      scroller: () => {
        return rootRef.current;
      }
    }
  );
};

const emptyKeys = { all: false, keys: new Set() };

const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  dataState: DataState<K, D>
) => {
  const keys = dataState.data.map((value: { data: D; metadata: Metadata<K> }) => {
    return value.metadata.key;
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: dataState.totalSize };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};

const defaultLoadingIndicator = (
  <SkeletonContainer minimumCount={25}>
    {() => {
      return (
        <Flex height="12x" align="center">
          <Skeleton height="4x" />
        </Flex>
      );
    }}
  </SkeletonContainer>
);

const defaultLoadMoreIndicator = (
  <div class={LOADMORE_STYLE_CLASS}>
    <SkeletonContainer minimumCount={3}>
      {() => {
        return (
          <Flex height="12x" align="center">
            <Skeleton height="4x" />
          </Flex>
        );
      }}
    </SkeletonContainer>
  </div>
);

/**
 * Adjust the DataState as needed if it contains more than needed for the specified range
 */
const adjustDataState = <K, D>(dataState: DataState<K, D>, range: Range | null) => {
  if (range) {
    // prevent range offset from being larger than the data state
    const safeRangeOffset = Math.min(range.offset, dataState.offset + dataState.totalSize);
    const diff = safeRangeOffset - dataState.offset;
    if (diff > 0) {
      dataState = {
        offset: safeRangeOffset,
        data: dataState.data.slice(diff, diff + range.count),
        totalSize: dataState.totalSize,
        sizePrecision: dataState.sizePrecision
      };
    }
  }
  return dataState;
};
